import { useAuth } from "../hooks/useAuth";
import { useStudents } from "./useStudents";
import { useTenantHeader } from "./useTenantHeader";
import { useSessionClass } from "../contexts/SessionClassContext";
import { useSessions } from "./useSession";
import { useEffect, useState } from "react";

// Storage keys for student's classId and sessionId
const STUDENT_CLASS_ID_KEY = 'studentClassId';
const STUDENT_SESSION_ID_KEY = 'studentSessionId';

/**
 * Custom hook to fetch the current logged-in student's data
 * Uses the email (mobile number) from login response to fetch student by mobile number
 * Requires sessionId and classId to be passed to the API
 * Uses stored values from localStorage (from previous login) or context/active session as fallback
 * Once student data is fetched, updates stored values from student's actual record
 * This should be used in all student-related pages to avoid multiple API calls
 */
export const useCurrentStudent = () => {
  const { user } = useAuth();
  const { tenantLoading } = useTenantHeader();
  const { selectedSession, selectedClass } = useSessionClass();
  const { useStudentByMobileNo, useStudentByStudentCode, useStudentById } = useStudents(tenantLoading);
  
  // Get schoolId from user
  const schoolId = user?.schoolId;
  
  // Get active session for the school (fallback)
  const { activeSessionQuery } = useSessions(schoolId ?? undefined);
  
  // Get stored classId and sessionId from localStorage (from previous login)
  const [storedClassId, setStoredClassId] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem(STUDENT_CLASS_ID_KEY);
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  });
  
  const [storedSessionId, setStoredSessionId] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem(STUDENT_SESSION_ID_KEY);
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  });
  
  // Priority: Use stored values > selected context > active session
  // For fetching, use: stored values > selected context > active session
  const sessionIdForFetch = storedSessionId ?? selectedSession?.id ?? activeSessionQuery.data?.id;
  const classIdForFetch = storedClassId ?? selectedClass?.id;
  
  // Check if user is a student
  const isStudent = user?.roles?.includes('STUDENT') || false;
  
  // Get email (which is actually mobile number) from login response
  const studentMobile = user?.email || "";
  
  // Validate if it's a mobile number (not an email)
  // Mobile numbers are typically 10 digits and don't contain '@'
  const isValidMobile = studentMobile && 
    !studentMobile.includes('@') && 
    /^\d{10}$/.test(studentMobile.replace(/\D/g, ''));
  
  // Get selected student ID and code from storage
  const selectedStudentIdStr = sessionStorage.getItem('selectedStudentId') || localStorage.getItem('selectedStudentId');
  const selectedStudentId = selectedStudentIdStr ? parseInt(selectedStudentIdStr, 10) : null;
  const selectedStudentCode = sessionStorage.getItem('selectedStudentCode') || localStorage.getItem('selectedStudentCode');
  
  // Fetch logic priority: By Student ID > By Student Code > By Mobile Number
  const shouldFetchById = isStudent && !!selectedStudentId && selectedStudentId > 0 && !tenantLoading;
  const shouldFetchByCode = isStudent && !shouldFetchById && !!selectedStudentCode && !tenantLoading;
  const shouldFetchByMobile = isStudent && !shouldFetchById && !shouldFetchByCode && isValidMobile && !!sessionIdForFetch && !!classIdForFetch && !tenantLoading;
  
  // Fetch queries
  const studentByIdQuery = useStudentById(
    shouldFetchById ? selectedStudentId! : undefined
  );

  const studentByCodeQuery = useStudentByStudentCode(
    shouldFetchByCode ? selectedStudentCode! : undefined
  );
  
  const studentByMobileQuery = useStudentByMobileNo(
    shouldFetchByMobile ? studentMobile : undefined,
    shouldFetchByMobile ? sessionIdForFetch : undefined,
    shouldFetchByMobile ? classIdForFetch : undefined
  );
  
  const studentData = shouldFetchById 
    ? studentByIdQuery.data 
    : (shouldFetchByCode ? studentByCodeQuery.data : studentByMobileQuery.data);
  const studentLoading = shouldFetchById 
    ? studentByIdQuery.isLoading 
    : (shouldFetchByCode ? studentByCodeQuery.isLoading : studentByMobileQuery.isLoading);
  const studentError = shouldFetchById 
    ? studentByIdQuery.error 
    : (shouldFetchByCode ? studentByCodeQuery.error : studentByMobileQuery.error);
  const refetchStudent = shouldFetchById 
    ? studentByIdQuery.refetch 
    : (shouldFetchByCode ? studentByCodeQuery.refetch : studentByMobileQuery.refetch);
  
  // Update stored values when student data is fetched
  useEffect(() => {
    if (studentData) {
      // Always update stored values from student's actual record (handles promotion updates)
      if (studentData.schoolClassId) {
        const newClassId = studentData.schoolClassId;
        localStorage.setItem(STUDENT_CLASS_ID_KEY, newClassId.toString());
        setStoredClassId(newClassId);
      }
      
      if (studentData.sessionId) {
        const newSessionId = studentData.sessionId;
        localStorage.setItem(STUDENT_SESSION_ID_KEY, newSessionId.toString());
        setStoredSessionId(newSessionId);
      }
    }
    // Note: Context updates (selectedClass, selectedSession) are handled by Layout component
    // which has access to classesQuery and sessionsQuery
  }, [studentData]);
  
  // Find selected student summary from user.students (login response) for instant display before API finishes
  const selectedStudentSummary = user?.students?.find(
    (s: any) => (selectedStudentId && s.id === selectedStudentId) || (selectedStudentCode && s.studentCode === selectedStudentCode)
  ) || (user?.students && user.students.length > 0 ? user.students[0] : null);

  return {
    studentData,
    selectedStudentSummary,
    studentLoading,
    studentError,
    refetchStudent,
    isStudent,
  };
};
